//@flow
import { connect } from 'react-redux';
import { clientSelector } from 'common/redux/selectors/queryParamsSelectors';
import { personnelSelector } from 'common/redux/selectors/sessionSelector';
import BulkGeocodeEditView from './BulkGeocodeEditView';
import { withRouter } from 'common/components/core/router';
import compose from 'recompose/compose';
import withPropsOnChange from 'recompose/withPropsOnChange';
import withHandlers from 'recompose/withHandlers';
import withStateHandlers from 'recompose/withStateHandlers';
import lifecycle from 'recompose/lifecycle';
import { getGroupByFromEntitySettings } from 'common/redux/selectors/clientSelector';
import * as Immutable from 'immutable';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';

import FilterListBuilder from 'common/models/helpers/FilterListBuilder';

import GeocodeEditUtils, {
    GeocodeFilterUtils
} from 'common/utils/GeocodeEditUtils';
import { GeocodeEditLocation } from 'common/models/internal/GeocodeEditLocation';
import { GroupedGeocodeEditLocation } from 'common/models/internal/GroupedGeocodeEditLocation';
import { showToast } from 'common/redux/actions/toast';
import { ToastTypes } from 'common/components/core/toast/Toast';
import { translate } from 'react-i18next';
import LocationGeocodeEditSelectors from 'common/redux/selectors/locationGeocodeEdit';
import DataUtils from 'common/utils/DataUtils';
import { getPlatformSettings } from 'common/modules/platform-settings';
import _ from 'lodash';
import { RoleOperationEnum } from 'common/modules/rbac';

const mapStateToProps = (state, props) => {
    const personnel = personnelSelector(state);
    const isEditable =
        personnel?.hasPermission(RoleOperationEnum.UPDATE_LOCATIONS) || false;
    const clientId = clientSelector(state, props);
    const groupBy = getGroupByFromEntitySettings(state, props);
    const { localeSettings } = getPlatformSettings();
    const geocodeValidatedGeometries = LocationGeocodeEditSelectors.getValidatedGeometries(
        state,
        props
    );
    let defaultCountryCode = null;

    if (localeSettings) {
        defaultCountryCode = localeSettings.countryCode;
    }
    return {
        isEditable,
        clientId,
        groupBy,
        defaultCountryCode,
        geocodeValidatedGeometries
    };
};

const actions = {
    showToastAction: showToast
};
type GeocodeInitialState = {
    expandedLocationId: ?string,
    selectedLocationId: ?string,
    expandedGroupId: ?string,
    positionInSortedList?: number,
    groupPositionInList?: number,
    searchText: string,
    precision: string,
    allLocations: Immutable.List<GeocodeEditLocation>,
    currentTab: string,
    isBulkGeocodeInProgress: boolean,
    isValidateGeocodesActive: boolean,
    isValidatingGeocodes: boolean
};
type AddressChangeType = {
    locationEntity: GroupedGeocodeEditLocation | GeocodeEditLocation,
    latLng: { lat: string, lng: string }
};

const GeocodeStateHandlers = compose(
    withStateHandlers(
        ({
            geocodeEditLocations,
            geocodeValidatedGeometries,
            groupBy
        }): GeocodeInitialState => {
            return {
                checkedCardIds: [],
                listFilters: GeocodeFilterUtils.DEFAULT_FILTERS,
                appliedFilters: {},
                expandedLocationId: null,
                selectedLocationId: null,
                expandedGroupId: null,
                positionInSortedList: undefined,
                groupPositionInList: undefined,
                searchText: '',
                precision: 'LOW',
                allLocations: GeocodeEditUtils.getEditGeocodeLocationsList({
                    geocodeEditLocations,
                    geocodeValidatedGeometries,
                    groupBy
                }),
                currentTab: 'INDIVIDUAL',
                isBulkGeocodeInProgress: false,
                isValidateGeocodesActive: false,
                isValidatingGeocodes: false
            };
        },
        {
            setIsValidateGeocodesActive: () => isValidateGeocodesActive => {
                return {
                    isValidateGeocodesActive
                };
            },
            setIsValidatingGeocodes: () => isValidatingGeocodes => {
                return {
                    isValidatingGeocodes
                };
            },
            setBulkGeocodeInProgress: () => isBulkGeocodeInProgress => {
                return {
                    isBulkGeocodeInProgress
                };
            },
            setListFilters: () => listFilters => {
                return {
                    listFilters
                };
            },
            setAppliedFilters: () => appliedFilters => {
                return {
                    appliedFilters
                };
            },
            setSearchText: () => searchText => {
                return {
                    searchText
                };
            },
            setSelectedLocationId: () => selectedLocationId => {
                return {
                    selectedLocationId
                };
            },
            setCheckedCardIds: () => checkedCardIds => {
                return {
                    checkedCardIds
                };
            },
            setExpandedLocationId: () => expandedLocationId => {
                return {
                    expandedLocationId
                };
            },
            resetExpandedLocationId: () => () => {
                return {
                    expandedLocationId: null
                };
            },
            setActiveGroup: () => (expandedGroupId, groupPositionInList) => {
                return {
                    expandedGroupId,
                    groupPositionInList
                };
            },
            resetActiveGroup: () => () => {
                return {
                    expandedGroupId: null,
                    groupPositionInList: undefined
                };
            },
            setActiveLocationState: () => ({
                positionInSortedList,
                groupPositionInList,
                expandedLocationId,
                selectedLocationId,
                expandedGroupId
            }) => {
                return {
                    expandedLocationId,
                    selectedLocationId,
                    positionInSortedList,
                    groupPositionInList,
                    expandedGroupId
                };
            },
            resetPositionInSortedList: () => () => {
                return {
                    positionInSortedList: undefined
                };
            },
            resetActiveLocation: () => () => {
                return {
                    expandedLocationId: null,
                    selectedLocationId: null,
                    positionInSortedList: undefined
                };
            },
            setAllLocations: () => (
                allLocations,
                positionInSortedList = undefined,
                groupPositionInList = undefined
            ) => {
                return {
                    allLocations,
                    positionInSortedList,
                    groupPositionInList
                };
            },
            setCurrentTab: () => currentTab => {
                return {
                    currentTab
                };
            }
        }
    ),
    withHandlers({
        handleTabChange: ({ setCurrentTab }) => currentTab => {
            setCurrentTab(currentTab);
        },
        handleListEscape: ({
            selectedLocationId,
            expandedLocationId,
            resetActiveLocation,
            resetExpandedLocationId
        }) => () => {
            if (expandedLocationId) {
                resetExpandedLocationId();
            } else if (selectedLocationId) {
                resetActiveLocation();
            }
        },
        handleListEnter: ({
            selectedLocationId,
            expandedLocationId,
            setExpandedLocationId
        }) => () => {
            if (selectedLocationId && !expandedLocationId) {
                setExpandedLocationId(selectedLocationId);
            }
        },
        handleSearchTextChange: ({
            setSearchText,
            setCheckedCardIds
        }) => searchText => {
            setSearchText(searchText);
            setCheckedCardIds([]);
        },
        handleFiltersChange: ({
            setAppliedFilters,
            setCheckedCardIds
        }) => appliedFilters => {
            setAppliedFilters(appliedFilters);
            setCheckedCardIds([]);
        },
        handleClearText: ({ setSearchText }) => () => {
            setSearchText('');
        }
    })
);

const ApiHandlers = compose(
    withHandlers({
        onVerifyFailure: ({ setAllLocations, allLocations }) => ({
            oldLocationEntities
        }) => {
            const {
                updatedAllLocations
            } = GeocodeEditUtils.resetMultipleLocationsVerification({
                allLocations,
                oldLocationEntities
            });
            setAllLocations(updatedAllLocations);
        },
        onEditLatLngFailure: ({ setAllLocations, allLocations }) => ({
            oldLocationEntities
        }) => {
            const {
                updatedAllLocations
            } = GeocodeEditUtils.resetMultipleLocationsToOld({
                allLocations,
                oldLocationEntities
            });
            setAllLocations(updatedAllLocations);
        }
    }),
    withHandlers({
        editLocationEntities: ({
            clientId,
            editEntityLatLngAction,
            onEditLatLngFailure,
            isValidateGeocodesActive,
            onCheckGeocode
        }) => ({
            updatedLocations,
            oldLocationEntities,
            onSuccess,
            onFailure
        }) => {
            if (!updatedLocations || updatedLocations.size === 0) {
                return;
            }
            const params = { clientId };
            const body = GeocodeEditUtils.getGeocodeEditsBody({
                updatedLocations
            });
            editEntityLatLngAction(params, body)
                .then(() => {
                    if (isValidateGeocodesActive) {
                        onCheckGeocode({
                            locations: updatedLocations.toArray()
                        });
                    }
                    onSuccess && onSuccess({ params, body });
                })
                .catch(error => {
                    onEditLatLngFailure({ oldLocationEntities });
                    onFailure &&
                        onFailure({ body, params, error, oldLocationEntities });
                });
        },
        verifyLocationEntities: ({
            clientId,
            verifyEntityAction,
            onVerifyFailure
        }) => ({ updatedLocations, oldLocationEntities }) => {
            if (!updatedLocations || updatedLocations.size === 0) {
                return;
            }
            const params = { clientId };
            const body = GeocodeEditUtils.getGeocodeEditsBody({
                updatedLocations,
                updateVerification: true
            });
            verifyEntityAction(params, body).catch(() =>
                onVerifyFailure({ oldLocationEntities })
            );
        }
    })
);

// used when polling
const GeocodePollHandlers = compose(
    withStateHandlers(
        {
            stopLocationPolling: null
        },
        {
            setStopLocationPollHandler: () => stopLocationPolling => {
                return {
                    stopLocationPolling
                };
            }
        }
    ),
    withHandlers({
        setGeocodeInactive: ({ setBulkGeocodeInProgress }) => () => {
            setBulkGeocodeInProgress(false);
        }
    }),
    withHandlers({
        pollLocationsSuccessHandler: ({
            t,
            allLocations,
            clientId,
            showToastAction,
            stopLocationPolling,
            geocodeLocationsSuccessAction,
            setGeocodeInactive,
            isValidateGeocodesActive,
            onCheckGeocode
        }) => (locations = []) => {
            const params = { clientId };
            if (
                !locations.filter(location => location.geocoded === false)
                    .length
            ) {
                stopLocationPolling && stopLocationPolling();
                geocodeLocationsSuccessAction(params, locations, allLocations);
                const toast = {
                    message: t('locationTable.bulkEditSuccessToast', {
                        count: locations.length
                    }),
                    type: ToastTypes.Success
                };
                setGeocodeInactive();
                showToastAction(toast, params);
                if (isValidateGeocodesActive) {
                    onCheckGeocode({ locations });
                }
            }
        }
    }),
    withHandlers({
        pollLocationHandler: ({
            clientId,
            pollLocationSearch,
            stopLocationPolling,
            setStopLocationPollHandler,
            setGeocodeInactive,
            pollLocationsSuccessHandler
        }) => (locations: Array<Object>) => {
            const params = { clientId };
            stopLocationPolling && stopLocationPolling();
            const body = {
                filters: new FilterListBuilder()
                    .add(
                        new FilterBuilder()
                            .withName('id')
                            .withOperation(FilterOperation.Equals)
                            .withValues(locations)
                    )
                    .build()
            };
            const stopLocationPollingHandler = pollLocationSearch(
                params,
                body,
                {
                    onSuccess: pollLocationsSuccessHandler,
                    onFailure: () => {
                        setGeocodeInactive();
                        stopLocationPolling && stopLocationPolling();
                    }
                }
            );
            setStopLocationPollHandler(stopLocationPollingHandler);
        }
    })
);

const GeocodeHandlers = compose(
    withHandlers({
        onCheckGeocode: ({ clientId, validateGeocodesAction }) => ({
            locations,
            onSuccess,
            onFaliure
        }) => {
            const params = { clientId };
            const locationIds = locations.map(location => location.id);
            validateGeocodesAction(params, locationIds)
                .then(() => onSuccess?.())
                .catch(() => onFaliure?.());
        }
    }),
    GeocodePollHandlers,
    withHandlers({
        onGeocode: ({
            clientId,
            geocodeLocationsAction,
            setBulkGeocodeInProgress,
            pollLocationHandler,
            setGeocodeInactive
        }) => locations => {
            const params = { clientId };
            const options = {
                onSuccess: locations => {
                    pollLocationHandler(locations);
                },
                onFailure: setGeocodeInactive
            };
            setBulkGeocodeInProgress(true);
            geocodeLocationsAction(params, locations, options);
        },
        onGeocodeCancel: ({
            setBulkGeocodeInProgress,
            stopLocationPolling
        }) => () => {
            setBulkGeocodeInProgress(false);
            stopLocationPolling && stopLocationPolling();
        }
    }),
    withHandlers({
        onBulkCheckGeocode: ({
            onCheckGeocode,
            setIsValidateGeocodesActive,
            setIsValidatingGeocodes,
            showToastAction,
            t,
            clientId
        }) => locations => {
            setIsValidatingGeocodes(true);

            onCheckGeocode({
                locations: locations.toArray(),
                onSuccess: () => {
                    showToastAction(
                        {
                            message: t('geocodeMap.entityValidationMessage', {
                                count: locations.size
                            }),
                            type: ToastTypes.Success
                        },
                        { clientId }
                    );
                    setIsValidatingGeocodes(false);
                },
                onFaliure: () => {
                    setIsValidatingGeocodes(false);
                    setIsValidateGeocodesActive(false);
                }
            });
        }
    }),
    withHandlers({
        onToggleValidateGeocodes: ({
            setIsValidateGeocodesActive,
            onBulkCheckGeocode,
            allLocations,
            appliedFilters,
            setListFilters,
            setAppliedFilters
        }) => value => {
            setIsValidateGeocodesActive(value);

            if (value) {
                onBulkCheckGeocode(allLocations);
                setListFilters(GeocodeFilterUtils.ALL_FILTERS);
            } else {
                setListFilters(GeocodeFilterUtils.DEFAULT_FILTERS);
                setAppliedFilters(_.omit(appliedFilters, 'GEOCODE_ACCURACY'));
            }
        },
        onGeocodeCancel: ({ setIsValidateGeocodesActive }) => () => {
            setIsValidateGeocodesActive(false);
        }
    })
);

const VerifyHandlers = compose(
    withHandlers({
        verifyEntities: ({
            setAllLocations,
            allLocations,
            verifyLocationEntities
        }) => ({ oldLocationEntities, verified }, event) => {
            event.stopPropagation();
            const {
                updatedAllLocations,
                updatedLocations
            } = GeocodeEditUtils.updateMultipleLocationsVerification({
                allLocations,
                editedLocations: oldLocationEntities,
                verified
            });
            setAllLocations(updatedAllLocations);
            verifyLocationEntities({
                updatedLocations,
                oldLocationEntities
            });
        }
    }),
    withHandlers({
        handleGroupVerify: ({ verifyEntities }) => (
            { locationEntity: groupedLocation },
            event
        ) => {
            verifyEntities(
                {
                    oldLocationEntities: groupedLocation.locations,
                    verified: groupedLocation.isGroupVerified()
                        ? !groupedLocation.isGroupVerified()
                        : true
                },
                event
            );
        },
        handleVerify: ({ verifyEntities }) => (
            { locationEntity: oldLocationEntity },
            event
        ) => {
            verifyEntities(
                {
                    oldLocationEntities: Immutable.List([oldLocationEntity]),
                    verified: oldLocationEntity.verified
                        ? !oldLocationEntity.verified
                        : true
                },
                event
            );
        }
    })
);
const AddressChangeHandlers = compose(
    withHandlers({
        editEntities: ({
            setAllLocations,
            allLocations,
            editLocationEntities
        }) => ({ oldLocationEntities, latLng }) => {
            const {
                updatedLocations,
                updatedAllLocations
            } = GeocodeEditUtils.updateMultipleLocationGeometryFields({
                editedLocations: oldLocationEntities,
                updatedLatLng: latLng,
                allLocations
            });
            setAllLocations(updatedAllLocations);
            editLocationEntities({
                updatedLocations,
                oldLocationEntities
            });
        },
        resetEntities: ({
            setAllLocations,
            allLocations,
            editLocationEntities
        }) => ({ oldLocationEntities }, event) => {
            event.stopPropagation();
            const {
                updatedLocations,
                updatedAllLocations
            } = GeocodeEditUtils.resetMultipleLocationsGeometryFields({
                oldLocationEntities,
                allLocations
            });
            setAllLocations(updatedAllLocations);
            editLocationEntities({
                updatedLocations,
                oldLocationEntities
            });
        }
    }),
    withHandlers({
        handleSingleAddressChange: ({ editEntities }) => ({
            latLng,
            locationEntity
        }) => {
            editEntities({
                oldLocationEntities: Immutable.List([locationEntity]),
                latLng
            });
        },
        handleGroupAddressChange: ({ editEntities, groupedLocations }) => ({
            locationEntity,
            latLng
        }) => {
            const { groupId } = locationEntity;
            const group = groupedLocations.find(
                locationGroup => locationGroup.groupId === groupId
            );
            editEntities({
                oldLocationEntities: group.locations,
                latLng
            });
        },
        handleResetLocation: ({ resetEntities }) => (
            { locationEntity },
            event
        ) => {
            resetEntities(
                { oldLocationEntities: Immutable.List([locationEntity]) },
                event
            );
        },
        handleResetGroupLocations: ({ resetEntities }) => (
            { locationEntity: groupedLocation },
            event
        ) => {
            resetEntities(
                { oldLocationEntities: groupedLocation.locations },
                event
            );
        }
    }),
    withHandlers({
        handleAddressChange: ({ editEntities }) => ({
            locationEntity,
            latLng
        }: AddressChangeType) => {
            editEntities({
                oldLocationEntities: Immutable.List([locationEntity]),
                latLng
            });
        },
        onApplySystemGeocode: ({ onGeocode }) => (locationEntity, event) => {
            event.stopPropagation();
            onGeocode([locationEntity]);
        },
        onApplySelectedSytemGeocodes: ({
            onGeocode,
            checkedCardIds,
            locations
        }) => () => {
            const locationsToGeocode = locations
                .filter(location => checkedCardIds.includes(location.id))
                .toArray();
            onGeocode(locationsToGeocode);
        },
        handleLatLngClick: ({ handleGroupAddressChange }) => (
            { locationEntity },
            event
        ) => {
            event.stopPropagation();
            handleGroupAddressChange({
                locationEntity,
                latLng: locationEntity.geometry.latLng
            });
        }
    })
);
const DerivedProps = withPropsOnChange(
    ['allLocations', 'searchText', 'listFilters', 'appliedFilters'],
    props => {
        const { allLocations, searchText, listFilters, appliedFilters } = props;
        const locations = GeocodeEditUtils.filterLocations({
            locations: allLocations,
            searchText,
            listFilters,
            appliedFilters
        });
        const groupedLocations = GeocodeEditUtils.getGroupedGeocodeLocationsList(
            locations
        );
        let nonGeocodedLocations = 0;
        let locationsWithLowConfidence = 0;
        locations.forEach(location => {
            if (!location.geometry || !location.geometry.latLng) {
                nonGeocodedLocations = nonGeocodedLocations + 1;
            } else if (location.geometry.confidence === 'LOW') {
                locationsWithLowConfidence = locationsWithLowConfidence + 1;
            }
        });

        const noOfLocationsThatNeedAttention =
            nonGeocodedLocations + locationsWithLowConfidence;

        return {
            locations,
            groupedLocations,
            noOfLocationsThatNeedAttention
        };
    }
);

const SelectionHandlers = compose(
    withHandlers({
        setActiveLocation: ({
            setActiveLocationState,
            expandedGroupId,
            groupedLocations,
            locations,
            isEditable
        }) => (locationEntity, { expand } = { expand: true }) => {
            const { id, groupId } = locationEntity;
            const positionInSortedList = locations.findIndex(
                loc => loc.id === id
            );
            let groupPositionInList;
            if (groupId && expandedGroupId !== groupId) {
                groupPositionInList = groupedLocations.findIndex(
                    groupLocation => groupLocation.groupId === groupId
                );
            }

            setActiveLocationState({
                positionInSortedList,
                groupPositionInList,
                expandedLocationId: isEditable && expand ? id : false,
                selectedLocationId: id,
                expandedGroupId: expand ? groupId : false
            });
        }
    }),
    withHandlers({
        handleGoToNextItem: props => () => {
            const {
                selectedLocationId,
                expandedLocationId,
                locations,
                setActiveLocation
            } = props;
            if (
                !DataUtils.isEmpty(expandedLocationId) &&
                DataUtils.isEmpty(selectedLocationId)
            ) {
                return;
            }
            const selectedLocationIndex = locations.findIndex(
                loc => loc.id === selectedLocationId
            );
            const selectedLocationEntity = locations.get(
                (selectedLocationIndex + 1) % locations.size
            );
            setActiveLocation(selectedLocationEntity, { expand: false });
        },
        handleGoToPreviousItem: props => () => {
            const {
                selectedLocationId,
                expandedLocationId,
                locations,
                setActiveLocation
            } = props;

            if (
                !DataUtils.isEmpty(expandedLocationId) &&
                DataUtils.isEmpty(selectedLocationId)
            ) {
                return;
            }
            const selectedLocationIndex = locations.findIndex(
                loc => loc.id === selectedLocationId
            );
            const selectedLocationEntity = locations.get(
                (selectedLocationIndex - 1) % locations.size
            );
            setActiveLocation(selectedLocationEntity, { expand: false });
        },
        handleHideSpider: ({ resetActiveLocation }) => () => {
            resetActiveLocation(null);
        },
        handleLocationSelect: ({ setActiveLocation, resetActiveLocation }) => ({
            locationEntity
        }) => {
            if (!locationEntity) {
                resetActiveLocation(null);
                return;
            }
            setActiveLocation(locationEntity);
        },
        handleToggleAll: ({
            allLocations,
            setCheckedCardIds,
            checkedCardIds
        }) => () => {
            if (checkedCardIds.length !== allLocations.size) {
                setCheckedCardIds(
                    allLocations.map(location => location.id).toArray()
                );
                return;
            }
            setCheckedCardIds([]);
        },
        handleCardToggle: ({
            setCheckedCardIds,
            checkedCardIds,
            resetPositionInSortedList
        }) => ({ locationEntity }) => {
            const { id } = locationEntity;
            let newCheckedCardIds = checkedCardIds;
            if (!checkedCardIds.includes(id)) {
                newCheckedCardIds = [...checkedCardIds, id];
            } else {
                newCheckedCardIds = checkedCardIds.filter(
                    checkedId => checkedId !== id
                );
            }
            setCheckedCardIds(newCheckedCardIds);
            if (newCheckedCardIds.length > 0) {
                resetPositionInSortedList();
            }
        },
        handleCardClick: ({
            setActiveLocation,
            resetExpandedLocationId,
            selectedLocationId,
            expandedLocationId
        }) => ({ locationEntity }) => {
            const { id } = locationEntity;
            if (id === selectedLocationId && id === expandedLocationId) {
                resetExpandedLocationId();
            } else {
                setActiveLocation(locationEntity);
            }
        },
        handleGroupCardClick: ({
            setActiveGroup,
            resetActiveGroup,
            expandedGroupId,
            groupedLocations
        }) => ({ locationEntity }) => {
            const { groupId } = locationEntity;
            if (groupId === expandedGroupId) {
                resetActiveGroup(null);
            } else {
                const groupListPositionIndex = groupedLocations.findIndex(
                    groupLocation => groupLocation.groupId === groupId
                );
                setActiveGroup(groupId, groupListPositionIndex);
            }
        }
    })
);

const locationSetter = {
    componentDidUpdate(prevProps) {
        const {
            setAllLocations,
            geocodeEditLocations,
            groupBy,
            geocodeValidatedGeometries,
            allLocations
        } = this.props;
        const {
            geocodeEditLocations: prevGeocodeEditLocations,
            geocodeValidatedGeometries: prevVerifiedGeometries
        } = prevProps;
        if (
            geocodeEditLocations &&
            geocodeEditLocations !== prevGeocodeEditLocations
        ) {
            setAllLocations(
                GeocodeEditUtils.getEditGeocodeLocationsList({
                    geocodeEditLocations,
                    geocodeValidatedGeometries,
                    groupBy
                })
            );
        }
        if (geocodeValidatedGeometries !== prevVerifiedGeometries) {
            setAllLocations(
                GeocodeEditUtils.updateMultipleLocationVerifiedGeometries({
                    allLocations,
                    geocodeValidatedGeometries
                })
            );
        }
    },
    componentWillUnmount() {
        const { clearGeocodeValidationDataAction, clientId } = this.props;
        clearGeocodeValidationDataAction({ clientId });
    }
};
const ManagedBulkGeocodeEditViewContainer = compose(
    withRouter,
    connect(mapStateToProps, actions),
    translate('entityManagement'),
    GeocodeStateHandlers,
    DerivedProps,
    GeocodeHandlers,
    ApiHandlers,
    SelectionHandlers,
    AddressChangeHandlers,
    VerifyHandlers,
    lifecycle(locationSetter)
)(BulkGeocodeEditView);

export { ManagedBulkGeocodeEditViewContainer as BulkGeocodeEditContainer };
export default ManagedBulkGeocodeEditViewContainer;
